import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { Download } from 'lucide-react'
import { Button } from '../ui/button'
import { Card } from '../ui/card'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import { formatTimestamp } from '../../utilities/messageHelpers'

function ConversationTranscript() {
  const { conversationId } = useParams()
  const [conversation, setConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchTranscript()
  }, [conversationId])

  const fetchTranscript = async () => {
    setIsLoading(true)
    try {
      const response = await axios.get(`/api/admin/conversation_transcripts/${conversationId}`)
      const { data } = response.data
      setConversation(data)
      setMessages(data.attributes?.messages || [])
    } catch (error) {
      console.error('Error fetching transcript:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const downloadCSV = async () => {
    try {
      const response = await axios.get(`/api/admin/conversation_transcripts/${conversationId}.csv`, {
        responseType: 'blob'
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `conversation_${conversationId}_transcript.csv`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Error downloading CSV:', error)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <LoadingSpinner size="1/5" color="purple" />
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="p-6">
        <Card className="p-8 text-center">
          <p className="text-gray-500">Conversation not found</p>
        </Card>
      </div>
    )
  }

  const userOne = conversation.attributes?.userOne
  const userTwo = conversation.attributes?.userTwo

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Conversation Transcript</h1>
          <p className="text-sm text-gray-600">
            Between {userOne?.preferredName} and {userTwo?.preferredName}
          </p>
        </div>
        <Button onClick={downloadCSV} variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Download CSV
        </Button>
      </div>

      <Card className="p-6">
        {messages.length === 0 ? (
          <p className="text-center text-gray-500">No messages in this conversation</p>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => {
              const attrs = message.attributes || {}
              const sender = attrs.sender || {}

              return (
                <div key={message.id} className="border-l-4 border-purple-200 pl-4 py-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-900">{sender.preferredName || sender.fullName}</span>
                    <span className="text-xs text-gray-500">{formatTimestamp(attrs.createdAt)}</span>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{attrs.body}</p>
                  {attrs.readAt && (
                    <span className="text-xs text-gray-500">Read at {formatTimestamp(attrs.readAt)}</span>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Card>
    </div>
  )
}

export default ConversationTranscript
